import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import AppSection from "@/components/app/AppSection";
import AppStickyFooter from "@/components/app/AppStickyFooter";
import { EmptyState } from "@/components/app/EmptyState";
import CTAButton from "@/components/app/CTAButton";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, BookOpenCheck, Plus, ArrowRight } from "lucide-react";

/**
 * AppMissionNew — Phase 3A
 * Page éditoriale de préparation. Deux points de départ :
 *  1) Mission depuis mes cartes manquantes (pré-remplit depuis user_card_states 'missing')
 *  2) Mission vide (l'utilisateur ajoutera les cartes depuis l'Explorer plus tard — Phase 3B)
 */
export default function AppMissionNew() {
    const navigate = useNavigate();
    const [tcgs, setTcgs] = useState([]);
    const [tcg, setTcg] = useState(null);
    const [title, setTitle] = useState("");
    const [error, setError] = useState("");
    const [creating, setCreating] = useState(false);
    const [activeBlock, setActiveBlock] = useState(null); // existing active mission blocks creation

    useEffect(() => {
        Promise.all([
            api.get("/catalog/tcgs"),
            api.get("/missions/active"),
        ]).then(([tcgsRes, activeRes]) => {
            setTcgs(tcgsRes.data);
            if (tcgsRes.data.length) setTcg(tcgsRes.data[0]);
            if (activeRes.data) setActiveBlock(activeRes.data);
        }).catch((e) => setError(formatApiError(e)));
    }, []);

    async function start(fromMissing) {
        if (!tcg) return;
        setCreating(true); setError("");
        try {
            const { data } = await api.post("/missions", { tcg_id: tcg._id, title: title.trim() || `Classeur ${tcg.name}` });
            if (fromMissing) {
                await api.post(`/missions/${data.id}/add-from-missing`);
            }
            // Always run preview to compute totals
            await api.post(`/missions/${data.id}/preview`);
            navigate(`/app/missions/${data.id}`);
        } catch (e) {
            setError(formatApiError(e));
            setCreating(false);
        }
    }

    if (activeBlock) {
        return (
            <>
                <AppHeader title="Préparer une mission" subtitle="Une mission à la fois" showBack onBack={() => navigate("/app/missions")} />
                <AppSection>
                    <EmptyState
                        icon={<BookOpenCheck className="h-6 w-6" />}
                        title="Tu as déjà une mission en cours"
                        description="Mission Dresseur, c’est une expédition à la fois. Termine ou archive ta mission actuelle pour en lancer une nouvelle."
                        action={
                            <CTAButton onClick={() => navigate(`/app/missions/${activeBlock._id}`)} icon={<ArrowRight className="h-4 w-4" />} iconPosition="right" testId="mission-new-open-active">
                                Ouvrir ma mission en cours
                            </CTAButton>
                        }
                    />
                </AppSection>
            </>
        );
    }

    return (
        <>
            <AppHeader title="Nouvelle mission" subtitle="Préparation d’expédition" showBack onBack={() => navigate("/app/missions")} />

            <AppSection>
                <div className="mt-2">
                    <p className="md-overline">PRÉPARATION DE MISSION</p>
                    <h1 className="mt-3 text-3xl font-black leading-tight tracking-tight text-md-text sm:text-4xl">
                        Prépare ton expédition.
                    </h1>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-md-text-2">
                        Une mission, c’est ton voyage de complétion. Tu choisis ce que tu cherches, on prépare la stack des dresseurs qui ont peut-être ce que tu manques.
                    </p>
                </div>

                {/* Pull-quote doctrine */}
                <div className="mt-7 border-l-2 border-md-secondary pl-4">
                    <p className="text-base italic leading-relaxed text-md-text">
                        « Les doubles des uns complètent les classeurs des autres. »
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-widest text-md-text-2">Doctrine Mission Dresseur</p>
                </div>
            </AppSection>

            {error && (
                <AppSection>
                    <div className="rounded-lg border border-md-danger/30 bg-md-danger/10 px-3 py-2 text-xs text-md-danger" data-testid="mission-new-error">{error}</div>
                </AppSection>
            )}

            <AppSection>
                <div className="space-y-4">
                    <div>
                        <label className="md-overline mb-2 block">Jeu de cartes</label>
                        <div className="grid grid-cols-1 gap-2">
                            {tcgs.map((t) => (
                                <button
                                    key={t._id}
                                    onClick={() => setTcg(t)}
                                    data-testid={`mission-new-tcg-${t.slug}`}
                                    className={`flex items-center justify-between rounded-xl border p-3 text-left transition ${tcg?._id === t._id ? "border-md-secondary bg-md-surface" : "border-md-border bg-md-surface hover:bg-md-surface-2"}`}
                                >
                                    <span className="text-sm font-semibold text-md-text">{t.name}</span>
                                    {tcg?._id === t._id && <Sparkles className="h-4 w-4 text-md-secondary" />}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="mission-title" className="md-overline mb-2 block">Titre (optionnel)</label>
                        <input
                            id="mission-title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Mon classeur Écarlate & Violet"
                            data-testid="mission-new-title"
                            maxLength={80}
                            className="block w-full rounded-lg border border-md-border bg-md-bg px-3 py-2.5 text-sm text-md-text outline-none focus:border-md-secondary"
                        />
                    </div>
                </div>
            </AppSection>

            <AppSection title="Choisis ton point de départ">
                <div className="space-y-3">
                    <StartCard
                        accent="primary"
                        title="Depuis mes cartes manquantes"
                        desc="On part de toutes les cartes que tu as marquées « manquant » dans l’Explorer. La mission est pré-remplie."
                        testId="mission-new-from-missing"
                        onClick={() => start(true)}
                        disabled={!tcg || creating}
                        loading={creating}
                    />
                    <StartCard
                        accent="gold"
                        title="Mission vierge"
                        desc="On crée un brouillon vide. Tu ajouteras les cartes depuis l’Explorer (Phase 3B)."
                        testId="mission-new-empty"
                        onClick={() => start(false)}
                        disabled={!tcg || creating}
                        loading={creating}
                    />
                </div>
            </AppSection>

            <div className="h-24" />
        </>
    );
}

function StartCard({ accent, title, desc, testId, onClick, disabled, loading }) {
    const accentBar = accent === "primary" ? "bg-md-primary" : "bg-md-secondary";
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className="group relative w-full overflow-hidden rounded-2xl border border-md-border bg-md-surface p-5 pl-6 text-left transition hover:border-md-text/30 disabled:opacity-60"
        >
            <span className={`absolute left-0 top-0 h-full w-1 ${accentBar}`} />
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-base font-bold tracking-tight text-md-text">{title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-md-text-2">{desc}</p>
                </div>
                {loading ? (
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-md-surface-2 text-md-text-2">
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
                    </span>
                ) : (
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-md-surface-2 text-md-text transition group-hover:bg-md-text group-hover:text-md-bg">
                        <Plus className="h-4 w-4" />
                    </span>
                )}
            </div>
        </button>
    );
}
